/**
 * PLM point count (Phase 2) — the browser's mirror of the arithmetic.
 *
 * The AUTHORITATIVE copy is server-side
 * (`Application/Analysis/Plm/PlmPointCountModels.cs` +
 * `AI_Plm_PointCount_Save_SP`): the API applies the exactly-1% guard and the
 * SP writes each fiber's result. This copy exists only so the grid's totals
 * update as the analyst types. Both are pinned to the SAME worked examples by
 * their tests, so they cannot drift.
 *
 * It replaces the legacy DevExpress workbook (`Loadpoint400` / `Loadpoint1000`,
 * PLMViewController.cs :208-282) — same formulas, no blob, no sheet-protection
 * password, no 6-fiber column cap.
 */

export type PointCountType = '400' | '1000';

export interface PlmPointCountSlide {
  fiberName: string;
  fiberSort: number;
  slideNo: number;
  /** "AP" — the asbestos points the analyst counted on this slide. */
  asbestosPoints: number;
  isTrace: boolean;
}

export interface PlmPointCountDetail {
  pointCountType: PointCountType | null;
  fiberNames: string[];
  slides: PlmPointCountSlide[];
}

export interface PlmPointCountTotals {
  fiberName: string;
  totalAp: number;
  totalNap: number;
  anyTrace: boolean;
  /** The numeric %AP, or null when the result is textual (Trace / below detection). */
  percent: number | null;
  /** What gets written to the exam row's Result. A "Trace" count persists as an
   *  EMPTY string, which is what legacy stores. */
  resultText: string;
}

/** 400 points over 8 slides of 50; 1000 over 10 slides of 100. */
export function slideCount(type: PointCountType): number {
  return type === '1000' ? 10 : 8;
}

/** Points on one slide — the AP + NAP total. */
export function pointsPerSlide(type: PointCountType): number {
  return type === '1000' ? 100 : 50;
}

/** Total points in the count — 400 or 1000, hence the name. */
export function totalPoints(type: PointCountType): number {
  return slideCount(type) * pointsPerSlide(type);
}

/** The "no asbestos found" text. Legacy hard-codes a different detection limit
 *  per count size — the bigger count sees further. */
export function belowDetection(type: PointCountType): string {
  return type === '1000' ? '<0.1%' : '<0.25%';
}

/** NAP is never entered — it is whatever is left on the slide. */
export function nonAsbestosPoints(ap: number, type: PointCountType): number {
  return pointsPerSlide(type) - Math.max(0, ap || 0);
}

/**
 * One fiber's derived totals — the legacy workbook's %AP formula, branch for
 * branch:
 *   `=IF(traceFlag>0,"Trace", IF(TotalAP=0,"<0.25%", ROUND(AP/(AP+NAP)*100,2)))`
 * where traceFlag is itself `=IF(TotalAP<1, IF(any Trace,1,0), "")`.
 */
export function fiberTotals(
  fiberName: string, slides: readonly PlmPointCountSlide[], type: PointCountType,
): PlmPointCountTotals {
  const per = pointsPerSlide(type);

  let totalAp = 0;
  let totalNap = 0;
  let anyTrace = false;

  for (const s of slides) {
    const ap = Math.max(0, s.asbestosPoints || 0);
    totalAp += ap;
    totalNap += per - ap;
    anyTrace = anyTrace || s.isTrace;
  }

  if (totalAp < 1 && anyTrace) {
    return { fiberName, totalAp, totalNap, anyTrace: true, percent: null, resultText: '' };
  }

  if (totalAp === 0) {
    return { fiberName, totalAp, totalNap, anyTrace, percent: null, resultText: belowDetection(type) };
  }

  const denominator = totalAp + totalNap;
  // Round half AWAY FROM ZERO to match SQL's ROUND() and C#'s
  // MidpointRounding.AwayFromZero — JS Math.round() rounds half UP, which
  // differs on negatives (unreachable here) and, more importantly, reads
  // differently from the other two copies.
  const raw = denominator === 0 ? 0 : (totalAp / denominator) * 100;
  const percent = Math.sign(raw) * Math.round(Math.abs(raw) * 100) / 100;

  return { fiberName, totalAp, totalNap, anyTrace, percent, resultText: percent.toFixed(2) };
}

/** Totals for every fiber in the grid, in display order. */
export function allTotals(
  slides: readonly PlmPointCountSlide[], type: PointCountType,
): PlmPointCountTotals[] {
  const byFiber = new Map<string, PlmPointCountSlide[]>();
  const order = new Map<string, number>();

  for (const s of slides) {
    const name = (s.fiberName ?? '').trim();
    if (!name) continue;
    if (!byFiber.has(name)) byFiber.set(name, []);
    byFiber.get(name)!.push(s);
    const seen = order.get(name);
    if (seen === undefined || s.fiberSort < seen) order.set(name, s.fiberSort);
  }

  return [...byFiber.entries()]
    .sort((a, b) => (order.get(a[0])! - order.get(b[0])!) || a[0].localeCompare(b[0]))
    .map(([name, rows]) => fiberTotals(name, rows, type));
}

/**
 * The "exactly 1%" recount rule (legacy `Dc_Accepting` :2982).
 *
 * A result of exactly 1% is the regulatory boundary for asbestos
 * classification, so it is indeterminate: legacy refuses the count and asks the
 * analyst to recount. It is exactly 4 asbestos points on a 400-point count, or
 * 10 on a 1000-point count — which is why the message names the fiber count
 * rather than the percentage.
 *
 * The SERVER applies this too; this copy just lets the dialog warn before the
 * analyst presses OK.
 *
 * @returns null when the count is acceptable, else the message to show.
 */
export function exactlyOnePercentRefusal(
  slides: readonly PlmPointCountSlide[], type: PointCountType,
): string | null {
  for (const t of allTotals(slides, type)) {
    if (t.percent === 1) {
      const label = type === '1000'
        ? '10 fibers on a 1000-point count'
        : '4 fibers on a 400-point count';
      return `Invalid count for ${label}: result is exactly 1%. Please recount.`;
    }
  }
  return null;
}

/**
 * Builds the grid for a set of asbestos fibers, carrying over whatever was
 * already counted.
 *
 * Legacy rebuilds the workbook's column set from the LIVE exam rows every time
 * PC Entry opens, so a fiber added since the count was started gets a fresh
 * column and one that was removed drops out (`PCEntry_Execute` :121). This does
 * the same, and keeps the existing counts for fibers that survived.
 */
export function buildGrid(
  fiberNames: readonly string[], existing: readonly PlmPointCountSlide[], type: PointCountType,
): PlmPointCountSlide[] {
  const kept = new Map<string, PlmPointCountSlide>();
  for (const s of existing) kept.set(`${(s.fiberName ?? '').trim()}#${s.slideNo}`, s);

  const out: PlmPointCountSlide[] = [];
  fiberNames.forEach((fiberName, fiberSort) => {
    for (let slideNo = 1; slideNo <= slideCount(type); slideNo++) {
      const prior = kept.get(`${fiberName}#${slideNo}`);
      out.push({
        fiberName,
        fiberSort,
        slideNo,
        asbestosPoints: prior?.asbestosPoints ?? 0,
        isTrace: prior?.isTrace ?? false,
      });
    }
  });
  return out;
}

// ── The PC Entry DRAFT (prototype v8 `renderPc` / `validatePc`, 2026-09-26) ──
//
// The dialog edits raw TEXT per cell, not numbers: a half-typed or invalid
// entry stays on screen and is flagged (red cell + footer message) instead of
// being silently coerced, and Apply is withheld until every cell is sound.

/** Key of one AP cell in the draft: fiber + slide. */
export const cellKey = (fiberName: string, slideNo: number): string =>
  `${fiberName.trim()}#${slideNo}`;

export interface PointCountDraft {
  /** What the analyst typed in each AP cell, by {@link cellKey}. */
  raw: Readonly<Record<string, string>>;
  /** Which Trace boxes are ticked, by {@link cellKey}. */
  trace: Readonly<Record<string, boolean>>;
}

/**
 * Seeds the draft from the stored count. A stored 0 shows as an EMPTY cell
 * behind the "0" placeholder — the prototype's look, and the model cannot tell
 * a counted zero from an untouched cell anyway.
 */
export function draftFromSlides(slides: readonly PlmPointCountSlide[]): PointCountDraft {
  const raw: Record<string, string> = {};
  const trace: Record<string, boolean> = {};
  for (const s of slides) {
    const k = cellKey(s.fiberName, s.slideNo);
    raw[k] = s.asbestosPoints > 0 ? String(s.asbestosPoints) : '';
    if (s.isTrace) trace[k] = true;
  }
  return { raw, trace };
}

/**
 * One typed AP cell → its value and its two possible faults.
 *
 *   malformed  not a whole number ≥ 0 ("2.5", "-1", "abc") — contributes 0
 *   over       a whole number above the slide's points — still CONTRIBUTES its
 *              value, so the slide's NAP goes negative and the row is flagged,
 *              exactly as the prototype shows it
 *
 * Empty is fine and means 0.
 */
export function parseApCell(
  raw: string | undefined, per: number,
): { ap: number; malformed: boolean; over: boolean } {
  const text = (raw ?? '').trim();
  if (text === '') return { ap: 0, malformed: false, over: false };
  const n = Number(text);
  if (!Number.isInteger(n) || n < 0) return { ap: 0, malformed: true, over: false };
  return { ap: n, malformed: false, over: n > per };
}

export const PC_HINT = 'Enter / ↑ ↓ move between cells. NAP = points per slide − AP.';

export interface PointCountValidation {
  /** Cells to paint red: malformed, or over the slide's points. */
  badCells: ReadonlySet<string>;
  /** Slides whose AP across ALL fibers together exceeds the slide's points. */
  overSlides: ReadonlySet<number>;
  /** The shared NAP per slide (index = slideNo − 1): points − Σ every fiber's AP. */
  slideNap: readonly number[];
  /** The footer line — the hint, or the first problem. */
  message: string;
  canApply: boolean;
}

/**
 * The prototype's `validatePc`: whole numbers only, and a slide cannot hold
 * more asbestos points than it has points — counted across ALL fibers on that
 * slide, which is the physical truth a per-fiber check misses (two fibers at 30
 * each on a 50-point slide is 60 points on 50).
 */
export function validatePointCount(
  fiberNames: readonly string[], draft: PointCountDraft, type: PointCountType,
): PointCountValidation {
  const per = pointsPerSlide(type);
  const badCells = new Set<string>();
  const overSlides = new Set<number>();
  const slideNap: number[] = [];
  let malformed = 0;

  for (let slideNo = 1; slideNo <= slideCount(type); slideNo++) {
    let sum = 0;
    for (const f of fiberNames) {
      const k = cellKey(f, slideNo);
      const cell = parseApCell(draft.raw[k], per);
      if (cell.malformed) { badCells.add(k); malformed++; }
      if (cell.over) badCells.add(k);
      sum += cell.ap;
    }
    if (sum > per) overSlides.add(slideNo);
    slideNap.push(per - sum);
  }

  const message = malformed
    ? 'AP must be a whole number ≥ 0.'
    : overSlides.size
      ? `AP on ${overSlides.size} slide(s) exceeds ${per} points.`
      : PC_HINT;

  return {
    badCells, overSlides, slideNap, message,
    canApply: malformed === 0 && overSlides.size === 0,
  };
}

/** The draft as the rows the server takes — every fiber × every slide. */
export function slidesFromDraft(
  fiberNames: readonly string[], draft: PointCountDraft, type: PointCountType,
): PlmPointCountSlide[] {
  const per = pointsPerSlide(type);
  const out: PlmPointCountSlide[] = [];
  fiberNames.forEach((fiberName, fiberSort) => {
    for (let slideNo = 1; slideNo <= slideCount(type); slideNo++) {
      const k = cellKey(fiberName, slideNo);
      out.push({
        fiberName, fiberSort, slideNo,
        asbestosPoints: parseApCell(draft.raw[k], per).ap,
        isTrace: draft.trace[k] === true,
      });
    }
  });
  return out;
}

/** Ticked Trace boxes for one fiber — the prototype's Total row shows the count. */
export function traceCount(fiberName: string, draft: PointCountDraft, type: PointCountType): number {
  let n = 0;
  for (let slideNo = 1; slideNo <= slideCount(type); slideNo++) {
    if (draft.trace[cellKey(fiberName, slideNo)]) n++;
  }
  return n;
}

/**
 * How the dialog's %AP row reads a fiber's total — the prototype's `fmtPct`:
 * "Trace", the detection limit, or "8%" / "2.5%" with no padding zeros.
 * DISPLAY ONLY: what is STORED in the exam row's Result is `resultText`, which
 * the server writes; nothing here changes that.
 */
export function fmtPercentDisplay(t: PlmPointCountTotals): string {
  if (t.percent === null) return t.resultText === '' ? 'Trace' : t.resultText;
  return `${+t.percent.toFixed(2)}%`;
}
